package org.gymly.ui.admin

import org.gymly.util.GymlyCurrency

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminClient
import org.gymly.model.AdminOrder
import org.gymly.model.AdminOrderStatus
import org.gymly.model.AdminOrderUpsert
import org.gymly.model.AdminPaymentMethod
import org.gymly.model.AdminPaymentUpsert
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.FileUploadIconButton
import org.gymly.ui.components.AdminDateField
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.downloadBytes
import org.gymly.util.formatMoney
import org.gymly.util.openProofFilePicker
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.todayIsoDate
import org.gymly.util.userMessage
import org.gymly.data.PickedMediaHolder
import org.gymly.model.PickedImagePayload
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.ui.window.DialogProperties
import kotlin.math.roundToLong
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import org.gymly.ui.components.VerticalScrollColumn

private enum class OrdersDateFilter(val label: String, val days: Int?) {
    Days7("7 días", 7),
    Days30("30 días", 30),
    All("Todos", null),
}

private sealed class OrdersDialog {
    data object Create : OrdersDialog()
    data class Edit(val order: AdminOrder) : OrdersDialog()
    data class Detail(val order: AdminOrder) : OrdersDialog()
    data class Delete(val order: AdminOrder) : OrdersDialog()
    data class RegisterPayment(val order: AdminOrder) : OrdersDialog()
}

private fun eurosToCents(raw: String): Long? {
    val cleaned = raw.trim().replace("€", "").replace(" ", "").replace(",", ".")
    if (cleaned.isEmpty()) return null
    val value = cleaned.toDoubleOrNull() ?: return null
    if (value <= 0) return null
    return (value * 100.0).roundToLong()
}

private fun centsToEurosInput(cents: Long): String {
    val intPart = cents / 100
    val frac = kotlin.math.abs(cents % 100).toInt()
    return "$intPart,${frac.toString().padStart(2, '0')}"
}

@Composable
fun AdminOrdersScreen() {
    val scope = rememberCoroutineScope()
    var orders by remember { mutableStateOf<List<AdminOrder>>(emptyList()) }
    var clients by remember { mutableStateOf<List<AdminClient>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dateFilter by remember { mutableStateOf(OrdersDateFilter.All) }
    var dialog by remember { mutableStateOf<OrdersDialog?>(null) }
    var saving by remember { mutableStateOf(false) }
    var successMsg by remember { mutableStateOf<String?>(null) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                orders = AdminRepo.api.listOrders()
                clients = AdminRepo.api.listClients()
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar las órdenes.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    val filtered = remember(orders, query, dateFilter) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        orders.filter { o ->
            val matchesQuery = q.isEmpty() ||
                o.clientName.lowercase().contains(q) ||
                o.itemsSummary().lowercase().contains(q) ||
                o.displayNumber().lowercase().contains(q) ||
                o.status.labelEs().lowercase().contains(q)
            val matchesDate = if (cutoff == null) {
                true
            } else {
                val created = o.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                created == null || created >= cutoff
            }
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "client",
        resetKey = listOf(query, dateFilter),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "status" -> { o -> o.status.name }
                "amount" -> { o -> o.totalAmountCents }
                "date" -> { o -> o.orderDate() }
                else -> { o -> o.clientName.lowercase() }
            }
        }
    }

    val total = filtered.size
    val paid = filtered.count { it.status == AdminOrderStatus.PAID }
    val pending = filtered.count { it.status.isPending() }
    val cancelled = filtered.count { it.status == AdminOrderStatus.CANCELLED }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        Column(Modifier.fillMaxSize()) {
            OrdersHeader(dateFilter) { dateFilter = it }
            Spacer(Modifier.height(16.dp))
            OrdersKpiRow(total, paid, pending, cancelled, compact)
            Spacer(Modifier.height(16.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            successMsg?.let {
                Text(it, color = AdminColors.Primary, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            OrdersDirectoryCard(
                orders = pageItems,
                totalCount = filtered.size,
                listPaging = listPaging,
                loading = loading,
                query = query,
                onQueryChange = { query = it },
                compact = compact,
                onCreate = { dialog = OrdersDialog.Create },
                onView = { dialog = OrdersDialog.Detail(it) },
                onEdit = { dialog = OrdersDialog.Edit(it) },
                onDelete = { dialog = OrdersDialog.Delete(it) },
                onRegisterPayment = { dialog = OrdersDialog.RegisterPayment(it) },
            )
        }
    }

    when (val d = dialog) {
        is OrdersDialog.Detail -> OrderDetailDialog(
            order = d.order,
            onDismiss = { dialog = null },
            onRegisterPayment = {
                dialog = OrdersDialog.RegisterPayment(d.order)
            },
        )
        is OrdersDialog.RegisterPayment -> OrderRegisterPaymentDialog(
            order = d.order,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onDone = { msg ->
                dialog = null
                successMsg = msg
                reload()
            },
            onError = { error = it },
            onSaving = { saving = it },
        )
        is OrdersDialog.Create -> OrderFormDialog(
            title = "Nueva orden",
            initial = null,
            clients = clients,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createOrder(upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo crear la orden.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is OrdersDialog.Edit -> OrderFormDialog(
            title = "Editar orden",
            initial = d.order,
            clients = clients,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updateOrder(d.order.id, upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo guardar la orden.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is OrdersDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar orden") },
            text = {
                Text("¿Seguro que deseas eliminar la orden ${d.order.displayNumber()}? Esta acción no se puede deshacer.")
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteOrder(d.order.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo eliminar la orden.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Eliminar")
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun OrdersHeader(
    dateFilter: OrdersDateFilter,
    onFilterChange: (OrdersDateFilter) -> Unit,
) {
    var menuOpen by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text("Órdenes", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                "Órdenes de venta y sus líneas desde /api/orders (cliente, ítems, total, estado, fecha).",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        Box {
            OutlinedButton(onClick = { menuOpen = true }, shape = RoundedCornerShape(10.dp)) {
                Text(dateFilter.label, color = AdminColors.Text)
            }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                OrdersDateFilter.entries.forEach { opt ->
                    DropdownMenuItem(
                        text = { Text(opt.label) },
                        onClick = {
                            onFilterChange(opt)
                            menuOpen = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun OrdersKpiRow(
    total: Int,
    paid: Int,
    pending: Int,
    cancelled: Int,
    compact: Boolean,
) {
    val kpiItems = listOf(
        Triple("Total de órdenes", total.toString(), AdminColors.Primary),
        Triple("Pagadas", paid.toString(), Color(0xFF2E7D32)),
        Triple("Pendientes", pending.toString(), Color(0xFFF59E0B)),
        Triple("Canceladas", cancelled.toString(), Color(0xFFC62828)),
    )
    if (compact) {
        // Celular: franja de indicadores de una línea.
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            kpiItems.forEach { (label, value, accent) ->
                AdminCompactKpiChip(label, value, accent, Modifier.weight(1f))
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            kpiItems.forEach { (label, value, accent) ->
                OrderKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun OrderKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun ColumnScope.OrdersDirectoryCard(
    orders: List<AdminOrder>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onView: (AdminOrder) -> Unit,
    onEdit: (AdminOrder) -> Unit,
    onDelete: (AdminOrder) -> Unit,
    onRegisterPayment: (AdminOrder) -> Unit,
) {
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Órdenes", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                OrderSearchField(query, onQueryChange)
                Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("+ Nueva orden", fontWeight = FontWeight.SemiBold)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Órdenes", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) {
                        OrderSearchField(query, onQueryChange)
                    }
                    Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text("+ Nueva orden", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("No hay órdenes todavía.", color = AdminColors.TextMuted)
            }
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    orders.forEach { o -> OrderMobileCard(o, onView, onEdit, onDelete, onRegisterPayment) }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                OrdersTable(
                    orders,
                    listPaging = listPaging,
                    onView,
                    onEdit,
                    onDelete,
                    onRegisterPayment,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun OrderSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun OrdersTable(
    orders: List<AdminOrder>,
    listPaging: AdminListPaging,
    onView: (AdminOrder) -> Unit,
    onEdit: (AdminOrder) -> Unit,
    onDelete: (AdminOrder) -> Unit,
    onRegisterPayment: (AdminOrder) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader("Cliente", "client", listPaging.sort, listPaging::onSort, Modifier.weight(1.4f))
            AdminListHeaderLabel("Ítems", Modifier.weight(1.3f))
            AdminSortableHeader("Total", "amount", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
            AdminSortableHeader("Fecha", "date", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
            AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.85f))
            AdminListHeaderLabel("Acciones", Modifier.width(220.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth(),
        ) {
            orders.forEach { o ->
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.4f), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(Color(o.status.dotColorArgb())),
                        )
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(o.displayNumber(), fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                            Text(o.clientName, color = AdminColors.TextMuted, fontSize = 12.sp)
                        }
                    }
                    Text(o.itemsSummary(), Modifier.weight(1.3f), color = AdminColors.Text, fontSize = 13.sp, maxLines = 2)
                    Text(
                        formatMoney(o.totalAmountCents.toInt().coerceAtLeast(0), GymlyCurrency.CODE),
                        Modifier.weight(0.8f),
                        color = AdminColors.Text,
                        fontSize = 13.sp,
                        maxLines = 1,
                    )
                    Text(o.orderDate(), Modifier.weight(0.9f), color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
                    Box(Modifier.weight(0.85f)) { OrderStatusBadge(o.status) }
                    Row(Modifier.width(220.dp), verticalAlignment = Alignment.CenterVertically) {
                        if (o.status == AdminOrderStatus.OPEN || o.status.isPending()) {
                            TextButton(onClick = { onRegisterPayment(o) }) {
                                Text("Cobrar", color = AdminColors.Primary, fontSize = 12.sp)
                            }
                        }
                        IconButton(onClick = { onView(o) }) {
                            Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                        }
                        IconButton(onClick = { onEdit(o) }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.Primary)
                        }
                        IconButton(onClick = { onDelete(o) }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun OrderMobileCard(
    order: AdminOrder,
    onView: (AdminOrder) -> Unit,
    onEdit: (AdminOrder) -> Unit,
    onDelete: (AdminOrder) -> Unit,
    onRegisterPayment: (AdminOrder) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(14.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                Modifier
                    .size(8.dp)
                    .clip(CircleShape)
                    .background(Color(order.status.dotColorArgb())),
            )
            Spacer(Modifier.width(8.dp))
            Column(Modifier.weight(1f)) {
                Text(order.displayNumber(), fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                Text(order.clientName, color = AdminColors.TextMuted, fontSize = 12.sp)
            }
            OrderStatusBadge(order.status)
        }
        Spacer(Modifier.height(8.dp))
        Text(order.itemsSummary(), color = AdminColors.Text, fontSize = 13.sp)
        Text(
            "${formatMoney(order.totalAmountCents.toInt().coerceAtLeast(0), GymlyCurrency.CODE)} · ${order.orderDate()}",
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
            if (order.status == AdminOrderStatus.OPEN || order.status.isPending()) {
                TextButton(onClick = { onRegisterPayment(order) }) {
                    Text("Cobrar", color = AdminColors.Primary, fontSize = 12.sp)
                }
            }
            IconButton(onClick = { onView(order) }) {
                Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted)
            }
            IconButton(onClick = { onEdit(order) }) {
                Icon(Icons.Outlined.Edit, null, tint = AdminColors.Primary)
            }
            IconButton(onClick = { onDelete(order) }) {
                Icon(Icons.Outlined.Delete, null, tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun OrderStatusBadge(status: AdminOrderStatus) {
    Text(
        status.labelEs(),
        color = Color(status.badgeFgArgb()),
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(999.dp))
            .background(Color(status.badgeBgArgb()))
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun OrderDetailDialog(
    order: AdminOrder,
    onDismiss: () -> Unit,
    onRegisterPayment: () -> Unit,
) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 420.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Text("Detalle de la orden", fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(14.dp))
            DetailRow("Orden", order.displayNumber())
            DetailRow("Cliente", order.clientName)
            DetailRow("Ítems", order.itemsSummary())
            DetailRow("Total", formatMoney(order.totalAmountCents.toInt().coerceAtLeast(0), GymlyCurrency.CODE))
            DetailRow("Fecha", order.orderDate())
            DetailRow("Estado", order.status.labelEs())
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                if (order.status == AdminOrderStatus.OPEN || order.status.isPending()) {
                    OutlinedButton(onClick = onRegisterPayment) {
                        Text("Registrar pago")
                    }
                    Spacer(Modifier.width(8.dp))
                }
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("Cerrar")
                }
            }
        }
    }
}

@Composable
private fun OrderRegisterPaymentDialog(
    order: AdminOrder,
    saving: Boolean,
    onDismiss: () -> Unit,
    onDone: (String) -> Unit,
    onError: (String) -> Unit,
    onSaving: (Boolean) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var method by remember { mutableStateOf(AdminPaymentMethod.CASH) }
    var methodMenu by remember { mutableStateOf(false) }
    var notes by remember { mutableStateOf("") }
    var paidDate by remember { mutableStateOf(todayIsoDate()) }
    var sendMail by remember { mutableStateOf(true) }
    var formError by remember { mutableStateOf<String?>(null) }
    var proofPayload by remember { mutableStateOf<org.gymly.model.PickedImagePayload?>(null) }
    var proofName by remember { mutableStateOf<String?>(null) }
    val proofHolder = remember { org.gymly.data.PickedMediaHolder() }
    var picking by remember { mutableStateOf(false) }

    Dialog(
        onDismissRequest = { if (!saving) onDismiss() },
        properties = androidx.compose.ui.window.DialogProperties(dismissOnClickOutside = false),
    ) {
        VerticalScrollColumn(
            Modifier
                .widthIn(max = 460.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Text("Registrar pago · ${order.displayNumber()}", fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(8.dp))
            Text(
                "${order.clientName} · ${formatMoney(order.totalAmountCents.toInt(), GymlyCurrency.CODE)}",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
            )
            Spacer(Modifier.height(14.dp))
            Text("Método", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            Box {
                OutlinedButton(onClick = { methodMenu = true }, modifier = Modifier.fillMaxWidth()) {
                    Text(method.labelEs(), color = AdminColors.Text)
                }
                DropdownMenu(expanded = methodMenu, onDismissRequest = { methodMenu = false }) {
                    AdminPaymentMethod.adminChoices().forEach { m ->
                        DropdownMenuItem(
                            text = { Text(m.labelEs()) },
                            onClick = {
                                method = m
                                methodMenu = false
                            },
                        )
                    }
                }
            }
            Spacer(Modifier.height(10.dp))
            AdminDateField(
                label = "Fecha del pago *",
                value = paidDate,
                onValueChange = { paidDate = it },
                placeholder = "AAAA-MM-DD o DD/MM/AAAA",
                outputFormat = AdminDateOutputFormat.ISO,
            )
            Spacer(Modifier.height(10.dp))
            if (method.requiresProofOrReference()) {
                Text("Referencia *", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    placeholder = { Text("Nº de referencia / operación", color = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
                Spacer(Modifier.height(10.dp))
                Text("Comprobante (imagen o PDF)", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    FileUploadIconButton(
                        onClick = {
                            picking = true
                            org.gymly.util.openProofFilePicker { picked ->
                                if (picked == null) {
                                    proofHolder.clear()
                                    scope.launch {
                                        picking = false
                                        formError = "No se seleccionó archivo."
                                    }
                                    return@openProofFilePicker
                                }
                                val payload = org.gymly.model.PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
                                proofHolder.set(payload, picked.fileName)
                                scope.launch {
                                    picking = false
                                    proofPayload = payload
                                    proofName = picked.fileName
                                    formError = null
                                }
                            }
                        },
                        tooltip = proofName?.let { "Cambiar comprobante" } ?: "Subir comprobante",
                        enabled = !saving && !picking,
                        tint = AdminColors.Primary,
                    )
                    if (proofName != null) {
                        Spacer(Modifier.width(8.dp))
                        Text(proofName!!, color = AdminColors.Text, fontSize = 12.sp)
                    }
                }
            } else if (method != AdminPaymentMethod.DIVISAS) {
                Text("Referencia (opcional)", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    placeholder = { Text("Nº de referencia / operación", color = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
            }
            Spacer(Modifier.height(10.dp))
            Row(verticalAlignment = Alignment.CenterVertically) {
                androidx.compose.material3.Checkbox(
                    checked = sendMail,
                    onCheckedChange = { sendMail = it },
                    colors = androidx.compose.material3.CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                )
                Text("Enviar recibo por correo al cliente", color = AdminColors.Text, fontSize = 13.sp)
            }
            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 12.sp)
            }
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        val ref = notes.trim()
                        val paidIso = parseFlexibleDateToIso(paidDate)
                        when {
                            paidIso == null -> formError = "Fecha inválida (DD/MM/AAAA o AAAA-MM-DD)."
                            method.requiresProofOrReference() && ref.isBlank() ->
                                formError = "La referencia es obligatoria para este método."
                            else -> {
                                formError = null
                                scope.launch {
                                    onSaving(true)
                                    try {
                                        val payment = AdminRepo.api.createPayment(
                                            AdminPaymentUpsert(
                                                orderId = order.id,
                                                method = method,
                                                amountCents = order.totalAmountCents,
                                                status = method.defaultCreateStatus(),
                                                notes = ref.takeIf { it.isNotBlank() },
                                                paidAt = "${paidIso}T12:00:00Z",
                                            ),
                                        )
                                        val proof = proofHolder.payload ?: proofPayload
                                        if (proof != null) {
                                            AdminRepo.api.uploadPaymentProof(payment.id, proof)
                                        }
                                        runCatching {
                                            val file = AdminRepo.api.downloadPaymentReceipt(payment.id)
                                            org.gymly.util.downloadBytes(file.fileName, file.mimeType, file.bytes)
                                        }
                                        var mailNote = ""
                                        if (sendMail) {
                                            runCatching {
                                                AdminRepo.api.emailPaymentReceipt(payment.id)
                                                mailNote = " · mail enviado"
                                            }.onFailure {
                                                mailNote = " · mail no enviado"
                                            }
                                        }
                                        onDone("Pago ${order.displayNumber()} registrado · recibo descargado$mailNote")
                                    } catch (e: Throwable) {
                                        formError = e.userMessage("No se pudo registrar el pago.")
                                        onError(formError!!)
                                    } finally {
                                        onSaving(false)
                                    }
                                }
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Guardar y recibo")
                }
            }
        }
    }
}

@Composable
private fun DetailRow(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

private fun clientMatchesOrderSearch(client: AdminClient, query: String): Boolean {
    val q = query.trim().lowercase()
    if (q.isEmpty()) return true
    return client.firstName.lowercase().contains(q) ||
        client.lastName.lowercase().contains(q) ||
        client.fullName.lowercase().contains(q) ||
        (client.email?.lowercase()?.contains(q) == true) ||
        (client.docId?.lowercase()?.contains(q) == true)
}

private fun filterClientsForOrderSearch(clients: List<AdminClient>, query: String): List<AdminClient> {
    val q = query.trim().lowercase()
    if (q.isEmpty()) return clients.take(12)
    if (q.length < 2) return emptyList()
    return clients.filter { clientMatchesOrderSearch(it, q) }.take(12)
}

@Composable
private fun OrderFormDialog(
    title: String,
    initial: AdminOrder?,
    clients: List<AdminClient>,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (AdminOrderUpsert) -> Unit,
) {
    var clientId by remember(initial) { mutableStateOf(initial?.clientId.orEmpty()) }
    var clientQuery by remember(initial) { mutableStateOf(initial?.clientName.orEmpty()) }
    var itemsLabel by remember(initial) { mutableStateOf(initial?.itemsSummary()?.takeIf { it != "—" }.orEmpty()) }
    var totalInput by remember(initial) {
        mutableStateOf(initial?.let { centsToEurosInput(it.totalAmountCents) }.orEmpty())
    }
    var status by remember(initial) { mutableStateOf(initial?.status ?: AdminOrderStatus.OPEN) }
    var formError by remember { mutableStateOf<String?>(null) }
    var clientMenu by remember { mutableStateOf(false) }
    var statusMenu by remember { mutableStateOf(false) }

    val filteredClients = remember(clients, clientQuery) {
        filterClientsForOrderSearch(clients, clientQuery)
    }
    val clientPickerOptions = remember(clients, clientQuery, filteredClients) {
        if (clientQuery.trim().isEmpty()) clients.take(12) else filteredClients
    }
    val showClientBrowse = clientMenu && clientQuery.trim().isEmpty() && clientPickerOptions.isNotEmpty()
    val showClientResults = clientMenu && clientQuery.trim().length >= 2 && clientPickerOptions.isNotEmpty()
    val showClientEmpty = clientMenu && clientQuery.trim().length >= 2 && filteredClients.isEmpty()

    Dialog(onDismissRequest = onDismiss) {
        VerticalScrollColumn(
            Modifier
                .widthIn(max = 480.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(14.dp))
            FormLabel("Cliente *")
            Text(
                "Busca por nombre, apellido, correo o cédula.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(bottom = 6.dp),
            )
            Box {
                OutlinedTextField(
                    value = clientQuery,
                    onValueChange = {
                        clientQuery = it
                        clientMenu = true
                        if (clientId.isNotEmpty()) {
                            val match = clients.firstOrNull { c -> c.id == clientId }
                            if (match == null || !clientMatchesOrderSearch(match, it)) {
                                clientId = ""
                            }
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    placeholder = { Text("Nombre, apellido, correo o cédula…", color = AdminColors.TextMuted) },
                    leadingIcon = { Icon(Icons.Outlined.Search, contentDescription = "Buscar cliente", tint = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
                DropdownMenu(
                    expanded = showClientBrowse || showClientResults,
                    onDismissRequest = { clientMenu = false },
                ) {
                    clientPickerOptions.forEach { c ->
                        DropdownMenuItem(
                            text = {
                                Column {
                                    Text(c.fullName, fontWeight = FontWeight.Medium)
                                    val subtitle = listOfNotNull(
                                        c.email,
                                        c.docId?.let { "Cédula $it" },
                                    ).joinToString(" · ")
                                    if (subtitle.isNotBlank()) {
                                        Text(subtitle, color = AdminColors.TextMuted, fontSize = 12.sp)
                                    }
                                }
                            },
                            onClick = {
                                clientId = c.id
                                clientQuery = c.fullName
                                clientMenu = false
                            },
                        )
                    }
                }
            }
            if (showClientEmpty) {
                Text(
                    "No hay cliente con ese dato.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 6.dp),
                )
            } else if (clientMenu && clientQuery.trim().length == 1) {
                Text(
                    "Escribe al menos 2 caracteres para buscar.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 6.dp),
                )
            }
            Spacer(Modifier.height(10.dp))
            FormLabel("Ítems")
            FormField(itemsLabel, { itemsLabel = it }, "Ej. plan mensual, suplementos…")
            Spacer(Modifier.height(10.dp))
            FormLabel("Total (Bs.)")
            FormField(totalInput, { totalInput = it }, "45,00")
            if (initial != null) {
                Spacer(Modifier.height(10.dp))
                FormLabel("Estado")
                Box {
                    OutlinedButton(onClick = { statusMenu = true }, modifier = Modifier.fillMaxWidth()) {
                        Text(status.labelEs(), color = AdminColors.Text)
                    }
                    DropdownMenu(expanded = statusMenu, onDismissRequest = { statusMenu = false }) {
                        listOf(AdminOrderStatus.OPEN, AdminOrderStatus.PAID, AdminOrderStatus.CANCELLED).forEach { s ->
                            DropdownMenuItem(
                                text = { Text(s.labelEs()) },
                                onClick = {
                                    status = s
                                    statusMenu = false
                                },
                            )
                        }
                    }
                }
            }
            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 12.sp)
            }
            Spacer(Modifier.height(18.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        val cents = eurosToCents(totalInput)
                        when {
                            clientId.isBlank() -> formError = "Selecciona un cliente de la lista."
                            itemsLabel.isBlank() -> formError = "Indica la descripción de los ítems."
                            cents == null -> formError = "Total inválido (usa formato 45,00)."
                            else -> {
                                formError = null
                                onSave(
                                    AdminOrderUpsert(
                                        clientId = clientId,
                                        status = if (initial == null) AdminOrderStatus.OPEN else status,
                                        itemsLabel = itemsLabel.trim(),
                                        totalAmountCents = cents,
                                    ),
                                )
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Guardar")
                }
            }
        }
    }
}

@Composable
private fun FormLabel(text: String) {
    Text(text, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(bottom = 4.dp))
}

@Composable
private fun FormField(value: String, onChange: (String) -> Unit, placeholder: String) {
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
